import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnDestroy, computed, effect, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ButtonDirective, SpinnerComponent } from '@coreui/angular-pro';
import { TranslatePipe, TranslateService } from 'i18n';

import {
  CompanionMessage, CompanionOverview, CompanionPlan, FORM_KINDS, HealthCompanionService, ObservationKind, PreviewItem, newClientRef
} from '../../../services/health-companion.service';
import { formatValue, isNumericKind, SCALE_MAX } from '../../../services/companion-format';

interface FormEntry { kind: ObservationKind; value: string; value2: string; text: string; skipped: boolean; context?: string }

/**
 * The "Heute" tab (plan §4.3–4.6, §13.2): today's state, the plan card (draft →
 * accept / change / decline; confirmed → steps + review), the conversation with
 * the editable PREVIEW of understood values, and the quick form with every area
 * as a reachable field (customer 2026-11-04). Nothing is stored from free text
 * before the member presses "save".
 */
@Component({
  selector: 'app-companion-today',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe, ButtonDirective, SpinnerComponent],
  templateUrl: './companion-today.component.html',
  styles: [`
    .chat { display: flex; flex-direction: column; gap: .5rem; max-height: 60vh; overflow-y: auto; padding: .25rem; }
    .bubble { border-radius: 14px; max-width: 92%; padding: .55rem .8rem; white-space: pre-wrap; overflow-wrap: anywhere; }
    .bubble--member { align-self: flex-end; background: var(--gh-sage-soft, #f1f5ef); }
    .bubble--companion { align-self: flex-start; background: var(--gh-surface-2, #faf7f0); border: 1px solid var(--gh-border, #e9e5dd); }
    .bubble--safety { background: #fde8e8; border-color: #f3b4b4; }
    .bubble--pending { color: var(--gh-text-muted, #6b6b6b); font-style: italic; }
    .bubble__meta { color: var(--gh-text-muted, #6b6b6b); font-size: .72rem; margin-top: .2rem; }
    .preview { border: 1px dashed var(--gh-green-action, #0E8F5E); border-radius: 12px; margin-top: .5rem; padding: .6rem .8rem; }
    .preview__row { align-items: center; border-top: 1px solid var(--gh-border, #e9e5dd); display: grid; gap: .4rem; grid-template-columns: 9rem 1fr auto; padding: .35rem 0; &:first-of-type { border-top: 0; } }
    .preview__row input, .preview__row select { max-width: 10rem; }
    .preview__row--unclear { background: #fff7e6; }
    .compose { display: grid; gap: .5rem; grid-template-columns: 1fr auto; margin-top: .75rem; }
    .form-section { border-top: 1px solid var(--gh-border, #e9e5dd); padding: .5rem 0; }
    .form-section summary { cursor: pointer; font-weight: 600; }
    .form-grid { display: grid; gap: .5rem; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); margin-top: .5rem; }
    .form-entry { display: grid; gap: .25rem; }
    .form-entry label { font-size: .85rem; font-weight: 600; }
    .scale { display: flex; flex-wrap: wrap; gap: .25rem; }
    .scale button { background: #fff; border: 1px solid var(--gh-border, #e9e5dd); border-radius: 6px; min-width: 2rem; padding: .2rem .4rem; }
    .scale button.on { background: var(--gh-green-action, #0E8F5E); border-color: var(--gh-green-action, #0E8F5E); color: #fff; }
    @media (max-width: 575.98px) { .preview__row { grid-template-columns: 1fr; } .compose { grid-template-columns: 1fr; } }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CompanionTodayComponent implements OnDestroy {
  private companion  = inject(HealthCompanionService);
  private translate  = inject(TranslateService);
  private destroyRef = inject(DestroyRef);

  overview = input.required<CompanionOverview>();
  readOnly = input(false);
  lang     = input('en');
  changed  = output<void>();

  messages = signal<CompanionMessage[]>([]);
  loadingMessages = signal(true);
  text     = signal('');
  sending  = signal(false);
  error    = signal<string | null>(null);
  busy     = signal(false);
  changeMode = signal(false);
  changeText = signal('');
  reviewDone = signal<number[]>([]);
  reviewNotes = signal('');
  formOpen = signal(false);
  bpReadings = signal<{ value: string; value2: string; pulse: string; context: string }[]>([{ value: '', value2: '', pulse: '', context: 'rest' }]);
  form = signal<FormEntry[]>(FORM_KINDS.filter(f => f.kind !== 'blood_pressure' && f.kind !== 'heart_rate')
    .map(f => ({ kind: f.kind, value: '', value2: '', text: '', skipped: false })));
  formSaved = signal<number | null>(null);

  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private pollStarted = 0;

  today     = computed(() => this.overview().today);
  draft     = computed(() => this.today().draft);
  confirmed = computed(() => this.today().confirmed_plan);
  lastPlan  = computed(() => this.today().last_plan);
  aiOn      = computed(() => this.overview().profile?.ai_enabled !== false);
  pending   = computed(() => this.messages().some(m => m.status === 'pending'));
  previewMessage = computed(() => [...this.messages()].reverse().find(m => m.preview_status === 'pending' && m.preview?.length) ?? null);
  weightUnit = computed(() => this.overview().profile?.weight_unit ?? 'kg');
  volumeUnit = computed(() => this.overview().profile?.volume_unit ?? 'ml');
  readonly scaleMax = SCALE_MAX;
  readonly formatValue = formatValue;
  readonly isNumeric = isNumericKind;

  constructor() {
    this.loadMessages();
    effect(() => {
      const c = this.confirmed();
      this.reviewDone.set(c?.review?.done ?? []);
      this.reviewNotes.set(c?.review?.notes ?? '');
    }, { allowSignalWrites: true });
  }

  ngOnDestroy(): void { this.stopPolling(); }

  // ----- conversation -----------------------------------------------------------

  loadMessages(): void {
    this.companion.messages().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: r => {
        this.messages.set(r.messages);
        this.loadingMessages.set(false);
        if (r.messages.some(m => m.status === 'pending')) this.startPolling(); else this.stopPolling();
      },
      error: () => this.loadingMessages.set(false)
    });
  }

  private startPolling(): void {
    if (this.pollTimer) return;
    this.pollStarted = Date.now();
    const tick = () => {
      this.pollTimer = null;
      if (Date.now() - this.pollStarted > 10 * 60 * 1000) return;
      this.companion.messages().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: r => {
          const wasPending = this.pending();
          this.messages.set(r.messages);
          if (r.messages.some(m => m.status === 'pending')) {
            const wait = Date.now() - this.pollStarted < 90_000 ? 2000 : 5000;
            this.pollTimer = setTimeout(tick, wait);
          } else if (wasPending) {
            this.changed.emit();
          }
        },
        error: () => { this.pollTimer = setTimeout(tick, 5000); }
      });
    };
    this.pollTimer = setTimeout(tick, 2000);
  }

  private stopPolling(): void {
    if (this.pollTimer) { clearTimeout(this.pollTimer); this.pollTimer = null; }
  }

  send(): void {
    const text = this.text().trim();
    if (!text || this.sending() || this.pending() || this.readOnly()) return;
    this.sending.set(true);
    this.error.set(null);
    this.companion.send(text, newClientRef()).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: r => {
        this.text.set('');
        this.sending.set(false);
        this.messages.update(list => [...list, ...r.messages.filter(m => !list.some(x => x.id === m.id))]);
        if (r.messages.some(m => m.status === 'pending')) this.startPolling(); else this.changed.emit();
      },
      error: err => { this.sending.set(false); this.error.set(this.errorKey(err)); }
    });
  }

  retry(m: CompanionMessage): void {
    this.companion.retry(m.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.loadMessages(),
      error: err => this.error.set(this.errorKey(err))
    });
  }

  // ----- preview ---------------------------------------------------------------------

  previewItems(m: CompanionMessage): PreviewItem[] { return m.preview ?? []; }

  removePreviewItem(m: CompanionMessage, index: number): void {
    const items = [...(m.preview ?? [])];
    items.splice(index, 1);
    this.messages.update(list => list.map(x => x.id === m.id ? { ...x, preview: items } : x));
  }

  updatePreviewItem(m: CompanionMessage, index: number, patch: Partial<PreviewItem>): void {
    const items = (m.preview ?? []).map((it, i) => i === index ? { ...it, ...patch, state: patch.state ?? (it.state === 'unclear' ? 'given' : it.state) } : it);
    this.messages.update(list => list.map(x => x.id === m.id ? { ...x, preview: items } : x));
  }

  savePreview(m: CompanionMessage): void {
    if (this.busy()) return;
    this.busy.set(true);
    this.companion.confirmPreview(m.id, m.preview ?? []).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: r => {
        this.busy.set(false);
        this.messages.update(list => list.map(x => x.id === m.id ? r.message : x));
        this.changed.emit();
        if (r.follow_up === 'plan' && this.aiOn()) this.propose();
      },
      error: err => { this.busy.set(false); this.error.set(this.errorKey(err)); }
    });
  }

  discardPreview(m: CompanionMessage): void {
    this.companion.discardPreview(m.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: r => this.messages.update(list => list.map(x => x.id === m.id ? r.message : x)),
      error: err => this.error.set(this.errorKey(err))
    });
  }

  // ----- plan -----------------------------------------------------------------------------

  propose(changeText?: string): void {
    if (this.busy() || this.pending() || this.readOnly() || !this.aiOn()) return;
    this.busy.set(true);
    this.error.set(null);
    this.companion.propose(changeText).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: r => {
        this.busy.set(false);
        this.changeMode.set(false);
        this.changeText.set('');
        this.messages.update(list => [...list, ...r.messages.filter(m => !list.some(x => x.id === m.id))]);
        this.startPolling();
      },
      error: err => { this.busy.set(false); this.error.set(this.errorKey(err)); }
    });
  }

  submitChange(): void {
    const t = this.changeText().trim();
    if (!t) return;
    this.propose(t);
  }

  confirmPlan(plan: CompanionPlan): void {
    if (this.busy()) return;
    this.busy.set(true);
    this.companion.confirmPlan(plan.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => { this.busy.set(false); this.loadMessages(); this.changed.emit(); },
      error: err => {
        this.busy.set(false);
        this.error.set(this.errorKey(err));
        if (err?.error?.error === 'plan_outdated') this.changed.emit();
      }
    });
  }

  declinePlan(plan: CompanionPlan): void {
    this.companion.declinePlan(plan.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.changed.emit(),
      error: err => this.error.set(this.errorKey(err))
    });
  }

  toggleDone(i: number): void {
    this.reviewDone.update(list => list.includes(i) ? list.filter(x => x !== i) : [...list, i]);
  }

  saveReview(plan: CompanionPlan): void {
    this.companion.reviewPlan(plan.id, this.reviewDone(), this.reviewNotes()).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.changed.emit(),
      error: err => this.error.set(this.errorKey(err))
    });
  }

  // ----- quick form ---------------------------------------------------------------------------

  setScale(entry: FormEntry, n: number): void {
    this.form.update(list => list.map(e => e === entry ? { ...e, value: e.value === String(n) ? '' : String(n), skipped: false } : e));
  }

  setEntry(entry: FormEntry, patch: Partial<FormEntry>): void {
    this.form.update(list => list.map(e => e === entry ? { ...e, ...patch } : e));
  }

  addBpReading(): void {
    this.bpReadings.update(list => [...list, { value: '', value2: '', pulse: '', context: 'rest' }]);
  }

  setBp(i: number, patch: Partial<{ value: string; value2: string; pulse: string; context: string }>): void {
    this.bpReadings.update(list => list.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  }

  unitFor(kind: ObservationKind): string {
    if (kind === 'weight') return this.weightUnit();
    if (kind === 'hydrogen_water') return this.volumeUnit();
    if (kind === 'sleep_hours') return 'h';
    return '';
  }

  submitForm(): void {
    if (this.busy() || this.readOnly()) return;
    const items: PreviewItem[] = [];
    const sessionKey = newClientRef();
    for (const r of this.bpReadings()) {
      if (r.value && r.value2) {
        items.push({ kind: 'blood_pressure', state: 'given', value: r.value, value2: r.value2, unit: 'mmHg', reference_day: 'today',
                     details: { context: r.context }, session_key: sessionKey });
      }
      if (r.pulse) {
        items.push({ kind: 'heart_rate', state: 'given', value: r.pulse, unit: 'bpm', reference_day: 'today',
                     details: { context: r.context }, session_key: sessionKey });
      }
    }
    for (const e of this.form()) {
      if (e.skipped) { items.push({ kind: e.kind, state: 'skipped', reference_day: 'today' }); continue; }
      if (isNumericKind(e.kind)) {
        if (e.value) items.push({ kind: e.kind, state: 'given', value: e.value, unit: this.unitFor(e.kind) || null, reference_day: 'today' });
      } else if (e.text.trim()) {
        items.push({ kind: e.kind, state: 'given', text: e.text.trim(), reference_day: 'today' });
      }
    }
    if (!items.length) return;
    this.busy.set(true);
    this.companion.addObservations(items, 'form', sessionKey).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: r => {
        this.busy.set(false);
        this.formSaved.set(r.observations.length);
        this.form.update(list => list.map(e => ({ ...e, value: '', value2: '', text: '', skipped: false })));
        this.bpReadings.set([{ value: '', value2: '', pulse: '', context: 'rest' }]);
        this.changed.emit();
      },
      error: err => { this.busy.set(false); this.error.set(this.errorKey(err)); }
    });
  }

  present(kind: ObservationKind): boolean { return this.today().present_kinds.includes(kind); }

  range(n: number): number[] { return Array.from({ length: n }, (_, i) => i); }

  private errorKey(err: { error?: { error?: string } }): string {
    const code = err?.error?.error ?? 'error';
    return ['answer_pending', 'rate_limited', 'plan_outdated', 'companion_expired', 'guardian_consent_required',
            'profile_not_confirmed', 'ai_disabled', 'no_preview', 'text_invalid'].includes(code) ? code : 'error';
  }
}
